import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import {
  Box,
  IconButton,
  IconButtonProps,
  Tooltip,
  styled,
} from "@mui/material";

import { Story } from "@baiki/interfaces";

import { usePlayer } from "@/components/player";

// ============================================================================
// Styled Components
// ============================================================================

const ProgressWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "progress" && prop !== "isActive" && prop !== "showProgress",
})<{ progress: number; isActive: boolean; showProgress: boolean }>(
  ({ theme, progress, isActive, showProgress }) => ({
    position: "relative",
    display: "inline-flex",

    // Light track circle (background ring)
    "&::before": {
      content: showProgress ? '""' : "none",
      position: "absolute",
      top: 2,
      left: 2,
      right: 2,
      bottom: 2,
      borderRadius: "50%",
      opacity: isActive ? 0.2 : 0,
      border: `2px solid ${theme.palette.primary.main}`,
      transition: "opacity 0.2s ease-in-out",
      pointerEvents: "none",
      zIndex: 1,
    },

    // Progress ring using conic-gradient
    "&::after": {
      content: showProgress ? '""' : "none",
      position: "absolute",
      top: 2,
      left: 2,
      right: 2,
      bottom: 2,
      borderRadius: "50%",
      opacity: isActive ? 1 : 0,
      background: `conic-gradient(
        ${theme.palette.primary.main} ${progress * 3.6}deg,
        transparent ${progress * 3.6}deg
      )`,
      // Mask to create ring effect
      mask: "radial-gradient(circle, transparent 60%, black 62%)",
      WebkitMask: "radial-gradient(circle, transparent 60%, black 62%)",
      transition: "opacity 0.2s ease-in-out",
      pointerEvents: "none",
      zIndex: 2,
    },

    "& > span": {
      position: "relative",
      zIndex: 3,
    },
  }),
);

interface PlayIconButtonProps extends IconButtonProps {
  story: Story | null;
  soft?: boolean; // soft pause/resume as oppose to hard stop/play
  showProgress?: boolean; // show circular progress indicator (default: false)
}

export const PlayIconButton: React.FC<PlayIconButtonProps> = ({
  story,
  soft,
  showProgress = false,
  ...props
}) => {
  const { t } = useTranslation();

  const {
    getElapsedPercent,
    isBufferingStory,
    isPlayingStory,
    pause,
    play,
    player,
    resume,
    stop,
  } = usePlayer();

  const isPlaying = useMemo(
    () => (story ? isPlayingStory(story) : false),
    [story, isPlayingStory],
  );

  const isBuffering = useMemo(
    () => (story ? isBufferingStory(story) : false),
    [story, isBufferingStory],
  );

  const icon = useMemo(() => {
    if (!isPlaying && !isBuffering)
      return (
        <PlayArrowRoundedIcon
          fontSize={props.size === "small" ? "small" : undefined}
        />
      );
    return soft ? (
      <PauseRoundedIcon
        fontSize={props.size === "small" ? "small" : undefined}
      />
    ) : (
      <StopRoundedIcon
        fontSize={props.size === "small" ? "small" : undefined}
      />
    );
  }, [isPlaying, isBuffering, props.size, soft]);

  const label = useMemo(() => {
    return isPlaying
      ? soft
        ? t("app.areas.playback.pause")
        : t("app.areas.playback.stop")
      : t("app.areas.playback.play");
  }, [isPlaying, t, soft]);

  const handleClick = useCallback(
    (_e: React.MouseEvent<HTMLButtonElement>) => {
      if (!story) return;

      if (isPlaying && soft) {
        pause();
        return;
      }

      if (isPlaying) {
        stop();
        return;
      }

      if (soft && story.id === player.story?.id) {
        resume(story);
        return;
      }

      play(story);
    },
    [story, isPlaying, soft, pause, stop, play, player, resume],
  );

  const progressPercent = useMemo(() => {
    if (!story || !isPlaying || !showProgress) return 0;
    return getElapsedPercent();
  }, [story, isPlaying, showProgress, getElapsedPercent]);

  return (
    <Tooltip title={label}>
      <ProgressWrapper
        progress={progressPercent}
        isActive={isPlaying}
        showProgress={showProgress}
      >
        <span>
          <IconButton
            color={isPlaying ? "primary" : undefined}
            disabled={isBuffering}
            onClick={handleClick}
            {...props}
          >
            {icon}
          </IconButton>
        </span>
      </ProgressWrapper>
    </Tooltip>
  );
};
